/**
 * Read/write adapter + validation for the real `payroll.getPayrollSettings` /
 * `payroll.updatePayrollSettings` wire shape (#22729 backend, #22730 this
 * pass) — pure, no React, mirrors `wagepro_store_data_centre.ts`'s
 * "permanent adapter shape + permanent read/write seams" pattern.
 *
 * `RealPayrollSettingsRecord` mirrors `payrollSettingsOutputSchema`
 * (`apps/api/src/trpc/routers/payroll.ts`) field-for-field — both
 * `getPayrollSettings` and `updatePayrollSettings` return exactly this
 * shape, and it is never partial: `getPayrollSettings` always resolves
 * `PAYROLL_SETTINGS_DEFAULTS` server-side for a brand-new entity with no
 * row yet, so the frontend never needs its own default fallback.
 *
 * Basis points, never floats (docs/money-rules.md) — `salary_advance_annual
 * _rate_bps`/`staff_loan_annual_rate_bps` are integers on the wire. The UI
 * edits them as a percentage string (e.g. "6.5") for human readability;
 * {@link parsePercentToBasisPoints}/{@link formatBasisPointsAsPercentInput}
 * are the ONLY two places that conversion happens, so the two can never
 * silently drift out of agreement with each other.
 */
import type {
  PayFrequency,
  PayrollSettings,
  ProrationMethod,
  RoundingRule,
} from './types';

/** The exact shape `payroll.getPayrollSettings`/`updatePayrollSettings` return (`payrollSettingsOutputSchema`, `apps/api/src/trpc/routers/payroll.ts`). */
export interface RealPayrollSettingsRecord {
  default_disbursement_account_id: string | null;
  disbursement_file_format: string | null;
  salary_advance_annual_rate_bps: number;
  salary_advance_max_installments: number;
  staff_loan_annual_rate_bps: number;
  staff_loan_max_installments: number;
  default_pay_frequency: PayFrequency;
  rounding_rule: RoundingRule;
  proration_method: ProrationMethod;
  overtime_hours_divisor: number;
  allow_negative_leave_balance: boolean;
}

/** The `{firm_id, entity_id}` pair every `payroll.*` call takes. */
export interface PayrollSettingsEntityInput {
  firm_id: string;
  entity_id: string;
}

/**
 * Partial patch accepted by `payroll.updatePayrollSettings` — mirrors the
 * backend's all-optional zod input exactly: every field the caller omits is
 * left untouched server-side (a partial upsert, never a full overwrite).
 */
export type PayrollSettingsPatch = Partial<RealPayrollSettingsRecord>;

/**
 * Derives the Settings screen's Payroll-tab shape from the full settings
 * record — the 4 fields that tab reads/writes. `allow_negative_leave_balance`
 * is deliberately NOT one of them: it is a leave policy, edited on the Leave
 * types tab (`leave_negative_balance_card.tsx`), so the Payroll tab never
 * seeds or saves it (see {@link buildPayrollTabPatch}).
 */
export function derivePayrollTabFields(
  record: RealPayrollSettingsRecord,
): PayrollSettings {
  return {
    default_pay_frequency: record.default_pay_frequency,
    rounding_rule: record.rounding_rule,
    proration_method: record.proration_method,
    overtime_hours_divisor: record.overtime_hours_divisor,
  };
}

// ── Validation bounds (mirrors `updatePayrollSettings`'s zod input exactly —
// `apps/api/src/trpc/routers/payroll.ts` — so a client-side rejection and the
// server's own rejection always agree; keeping the client validation loose
// enough to accept everything the server would is what "mirrors" means here,
// not merely "similar") ──────────────────────────────────────────────────────

const MAX_ANNUAL_RATE_BPS = 10_000; // 100% p.a. ceiling
const MIN_INSTALLMENTS = 1;
const MAX_INSTALLMENTS = 120;
const MIN_OVERTIME_DIVISOR = 1;
const MAX_OVERTIME_DIVISOR = 10_000;
export const MAX_DISBURSEMENT_FILE_FORMAT_LENGTH = 100;

export function isValidAnnualRateBps(bps: number): boolean {
  return Number.isInteger(bps) && bps >= 0 && bps <= MAX_ANNUAL_RATE_BPS;
}

export function isValidInstallmentCount(count: number): boolean {
  return (
    Number.isInteger(count) &&
    count >= MIN_INSTALLMENTS &&
    count <= MAX_INSTALLMENTS
  );
}

export function isValidOvertimeDivisor(divisor: number): boolean {
  return (
    Number.isInteger(divisor) &&
    divisor >= MIN_OVERTIME_DIVISOR &&
    divisor <= MAX_OVERTIME_DIVISOR
  );
}

export function isValidDisbursementFileFormat(text: string): boolean {
  return text.length <= MAX_DISBURSEMENT_FILE_FORMAT_LENGTH;
}

/**
 * Parses a user-typed annual-rate percentage (e.g. `"6.5"`) into integer
 * basis points (`650`) — docs/money-rules.md's basis-points scale (1% = 100
 * bps). Rounds to the nearest whole basis point (round-half-up) since a bps
 * is already the finest resolution the backend stores (0.01%). Returns
 * `null` for anything that doesn't parse to a plain decimal number — the
 * caller shows a validation error rather than silently coercing garbage
 * input to `0`.
 *
 * Deliberately does NOT compute this as `Math.round(Number(text) * 100)`.
 * That float multiply is not exact for plenty of ordinary-looking inputs —
 * `6.005 * 100 === 600.49999999999994` and `0.145 * 100 ===
 * 14.499999999999998` in IEEE754 — silently rounding DOWN a value that is
 * exactly (or, for a typed decimal, effectively) a half-basis-point case.
 * Instead this reads the decimal digits directly off the input string and
 * rounds on the third fraction digit, which is exact for any string input
 * regardless of binary floating-point representability.
 */
export function parsePercentToBasisPoints(text: string): number | null {
  const trimmed = text.trim();
  if (!/^-?\d+(\.\d*)?$/.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [wholeText, fractionText = ''] = unsigned.split('.');
  const whole = Number(wholeText);
  // Pad/truncate to exactly 3 fraction digits: the first 2 become the bps
  // fraction, the 3rd is the round-half-up decider. Any digits beyond the
  // 3rd are strictly less significant than 0.005 bps and are dropped.
  const fraction = `${fractionText}000`.slice(0, 3);
  const fractionBps = Number(fraction.slice(0, 2));
  const roundingDigit = Number(fraction[2]);
  const bps = whole * 100 + fractionBps + (roundingDigit >= 5 ? 1 : 0);
  return negative ? -bps : bps;
}

/** Inverse of {@link parsePercentToBasisPoints} — renders bps back into an editable percent string, e.g. `650` -> `"6.5"`. */
export function formatBasisPointsAsPercentInput(bps: number): string {
  return String(bps / 100);
}

/**
 * Parses a user-typed whole-number field (installment count, overtime
 * divisor) into an integer. Rejects decimals and non-numeric input outright
 * (`null`) rather than silently truncating a typed `"3.5"` to `3` — the
 * caller shows a validation error instead.
 */
export function parseIntegerInput(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '' || !/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * Builds the `updatePayrollSettings` patch for the Bank tab's two fields,
 * including ONLY the field(s) that actually changed from the currently
 * persisted settings (mirrors `wagepro_store_settings.ts`'s
 * `planOrganisationSave` diffing pattern for Organisation settings).
 *
 * This matters beyond avoiding a no-op write: `default_disbursement_account_id`
 * is entity-scoped, non-archived, bank/cash-asset-validated server-side
 * (`assertDisbursementAccount`, `apps/api/src/trpc/routers/payroll.ts`) on
 * EVERY value provided in the patch — including one that was already
 * persisted and hasn't changed. If the currently-saved account has since
 * been archived (or the account list failed to load and the draft still
 * holds the seeded id unmodified), unconditionally re-sending it would fail
 * the WHOLE save with a generic error, blocking an unrelated
 * `disbursement_file_format`-only edit the user actually meant to make
 * (adversarial-review finding, #22730). Diffing means an untouched field
 * is never resubmitted at all.
 */
export function buildDisbursementPatch(
  seeded: {
    default_disbursement_account_id: string | null;
    disbursement_file_format: string | null;
  },
  draft: { accountId: string; fileFormat: string },
): PayrollSettingsPatch {
  const patch: PayrollSettingsPatch = {};
  const nextAccountId = draft.accountId === '' ? null : draft.accountId;
  if (nextAccountId !== seeded.default_disbursement_account_id) {
    patch.default_disbursement_account_id = nextAccountId;
  }
  const nextFileFormat =
    draft.fileFormat.trim() === '' ? null : draft.fileFormat.trim();
  if (nextFileFormat !== seeded.disbursement_file_format) {
    patch.disbursement_file_format = nextFileFormat;
  }
  return patch;
}

/**
 * Builds the `updatePayrollSettings` patch for the Loans tab's four fields —
 * same "only the fields that actually changed" diffing rationale as
 * {@link buildDisbursementPatch}, applied to salary-advance/staff-loan rate
 * and installment-cap edits. Callers pass already-validated numeric values
 * (never the raw parse result) — this function assumes every field is a
 * valid, in-range integer.
 */
export function buildLoanPolicyPatch(
  seeded: RealPayrollSettingsRecord,
  draft: {
    salaryAdvanceRateBps: number;
    salaryAdvanceMaxInstallments: number;
    staffLoanRateBps: number;
    staffLoanMaxInstallments: number;
  },
): PayrollSettingsPatch {
  const patch: PayrollSettingsPatch = {};
  if (draft.salaryAdvanceRateBps !== seeded.salary_advance_annual_rate_bps) {
    patch.salary_advance_annual_rate_bps = draft.salaryAdvanceRateBps;
  }
  if (
    draft.salaryAdvanceMaxInstallments !==
    seeded.salary_advance_max_installments
  ) {
    patch.salary_advance_max_installments = draft.salaryAdvanceMaxInstallments;
  }
  if (draft.staffLoanRateBps !== seeded.staff_loan_annual_rate_bps) {
    patch.staff_loan_annual_rate_bps = draft.staffLoanRateBps;
  }
  if (draft.staffLoanMaxInstallments !== seeded.staff_loan_max_installments) {
    patch.staff_loan_max_installments = draft.staffLoanMaxInstallments;
  }
  return patch;
}

/**
 * Builds the `updatePayrollSettings` patch for the Settings screen's Payroll
 * tab's four fields — same diffing rationale as {@link buildDisbursementPatch}.
 *
 * `allow_negative_leave_balance` is INTENTIONALLY not accepted or emitted
 * here: that flag is a leave policy owned by the Leave types tab
 * (`leave_negative_balance_card.tsx`), which saves it on its own via a patch
 * containing only that field. The Payroll tab's patch simply never carries
 * it.
 */
export function buildPayrollTabPatch(
  seeded: RealPayrollSettingsRecord,
  draft: {
    defaultPayFrequency: PayFrequency;
    roundingRule: RoundingRule;
    prorationMethod: ProrationMethod;
    overtimeHoursDivisor: number;
  },
): PayrollSettingsPatch {
  const patch: PayrollSettingsPatch = {};
  if (draft.defaultPayFrequency !== seeded.default_pay_frequency) {
    patch.default_pay_frequency = draft.defaultPayFrequency;
  }
  if (draft.roundingRule !== seeded.rounding_rule) {
    patch.rounding_rule = draft.roundingRule;
  }
  if (draft.prorationMethod !== seeded.proration_method) {
    patch.proration_method = draft.prorationMethod;
  }
  if (draft.overtimeHoursDivisor !== seeded.overtime_hours_divisor) {
    patch.overtime_hours_divisor = draft.overtimeHoursDivisor;
  }
  return patch;
}

/** `true` when a patch built by any of the `build*Patch` functions above has no fields to send -- the caller should skip the network round trip entirely (mirrors `planOrganisationSave`'s `'noop'` branch). */
export function isEmptyPatch(patch: PayrollSettingsPatch): boolean {
  return Object.keys(patch).length === 0;
}
